import { onDelegatedEvent, setLoading, initAllModules, viewTransition, replacePageContent } from '../functions.js';

/**
 * Allows to navigate through pages without reloading the page.
 * 
 * Usage:
 *      <ajax-navigation data-selector=".pagination a" id="nav1">
 *       ... Your content
 *      </ajax-navigation>
 * 
 * Whenever the user clicks some ".pagination a" element, the content of this element will be replaced with the content of the new page.
 * 
 * Options:
 *   - data-selector:         Selector of the elements that will trigger the navigation
 *   - data-target:           Optional. Selector of the element that will be replaced with the new content
 *   - data-loading-selector: Optional. If specified, a ".is-loading" class will be added to this element while the content 
 *                            is being loaded. If not, the class is added directly to the ajax-navigation element.
 *   - data-scroll-to-top:    Optional. If specified, the page will scroll to the top of the target element after the content is loaded.
 * 
 * Public methods
 *   - refresh(): Refresh the content of the element
 * 
 * @since 2024-03-26 docs and public refresh() method
 * @since 2024-04-08 Prefetch on mouseover to increase speed
 * @since 2024-05-13 data-scroll-to-top
 */
export default class AjaxNavigationElement extends HTMLElement {

    _args = {};
    _abortController = null;
    _replaceTarget = null;
    _prefetched = {};
   
    connectedCallback() {

        this.style.display = 'block';

        this._args = this.dataset;
        
        if (!this._args.selector) { throw new Error('AjaxLoadOnClickModule: selector is required'); }
    
        this._replaceTarget = this.querySelector(this._args.target) || this;

        if (!this._replaceTarget.getAttribute('id')) {
            throw new Error('AjaxNavigation: target must have an id in order to be replaced');
        }

        onDelegatedEvent('click', this, this._args.selector, this._onClick.bind(this));
        onDelegatedEvent('mouseover', this, this._args.selector, this._onMouseOver.bind(this));
        
        this._reinitAbortController();
        
        this._bindedOnPopState = this._onPopState.bind(this);
        window.addEventListener('popstate', this._bindedOnPopState);
        
    }

    disconnectedCallback() {
        window.removeEventListener('popstate',  this._bindedOnPopState);
    }

    _bindedOnPopState = null;
    _onPopState(event) {
        this._navigate(document.location.href);
    }

    _onClick(event) {
        event.preventDefault();

        let realTarget = event.target.closest(this._args.selector);
        if (!realTarget) { return; }
        
        history.pushState({}, '', realTarget.href);
        this._navigate(realTarget.href);
    }

    _onMouseOver(event) {
        let anchor = event.selectorTarget;
        let href = anchor.href;
        if (!href) { return; }
        this._fetch(href);
    }

    _reinitAbortController() {
        if (this._abortController) {
            this._abortController.abort();
        }
        this._abortController = new AbortController();
    }

    _setThisLoading(loading) {
        let target = this._args.loadingSelector ? document.querySelector(this._args.loadingSelector) : this._replaceTarget;
        setLoading(target, loading);

        if (window.NProgress) {
            if (loading) {
                NProgress.start();
            }
            else {
                NProgress.done();
            }
        }
    }

    _fetch(url) {

        if (this._prefetched[url]) {
            return Promise.resolve(this._prefetched[url]);
        }

        let request = new Request(url, {
            headers: { 'X-Is-Ajax': '1' },
            signal: this._abortController.signal,
        });

        return fetch(request)
            .then(res => res.text())
            .then(res => {
                this._prefetched[url] = res; 
                return res;
            });
    }

    _navigate(url) {
        this._setThisLoading(true);
        this._reinitAbortController();

        this._fetch(url)
            .then(res => {
                this._setThisLoading(false);
                this._clearPrefetched();

                replacePageContent(this._replaceTarget, res, () => {

                    if (this.dataset.scrollToTop) {
                        this._replaceTarget.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                    }
                    // this._dispatchEvents();
                });

            }).catch(err => {
                if (err.name === 'AbortError') {
                    return;
                }
                this._setThisLoading(false);
                console.error(err);
            });
    }

    _clearPrefetched() {
        this._prefetched = {};
    }

    refresh() {
        this._navigate(document.location.href);
    }
    
}

window.customElements.define('ajax-navigation', AjaxNavigationElement);
